import moment, { Moment, MomentInput } from 'moment';
import {
  dateFormat,
  dateTimeFormatWithTimezone,
  localDataFormat,
} from '../constants/apiDateFormat';
import { Day } from '../constants/daysOfWeek';

const DAY_LABELS: Record<Day, { short: string; long: string; narrow: string }> = {
  sunday: { short: 'Sun', long: 'Sunday', narrow: 'S' },
  monday: { short: 'Mon', long: 'Monday', narrow: 'M' },
  tuesday: { short: 'Tue', long: 'Tuesday', narrow: 'T' },
  wednesday: { short: 'Wed', long: 'Wednesday', narrow: 'W' },
  thursday: { short: 'Thu', long: 'Thursday', narrow: 'T' },
  friday: { short: 'Fri', long: 'Friday', narrow: 'F' },
  saturday: { short: 'Sat', long: 'Saturday', narrow: 'S' },
};

function formatDayLabel(day: Day, format: 'narrow' | 'short' | 'long'): string {
  return DAY_LABELS[day][format] ?? day;
}

function getFormattedDate(date: MomentInput, format = dateFormat, fromFormat?: string) {
  return moment(date, fromFormat).format(format);
}

function getFormattedToday(format = dateFormat) {
  return getFormattedDate(undefined, format);
}
// only for date with timezone
function getFormattedLocalDate(date: MomentInput, format?: string) {
  return moment(date).local().format(format);
}

function getTomorrow() {
  return moment().add(1, 'day');
}

function getDaysAgo(count: number) {
  return moment().subtract(count, 'day');
}

function getDayBeforeDate(date: MomentInput, count: number) {
  return moment(date).subtract(count, 'day');
}

function getDayAfterToday(count: number) {
  return moment().add(count, 'day');
}

function getDayAfterDate(date: MomentInput, count: number) {
  return moment(date).add(count, 'day');
}

function getMonthsAgo(count: number) {
  return moment().subtract(count, 'month');
}

function getYearsBeforeDate(date: MomentInput, count: number) {
  return moment(date).subtract(count, 'year');
}

function isToday(date: MomentInput, format?: string) {
  return moment(date, format).isSame(moment(), 'day');
}

function isTomorrow(date: MomentInput, format?: string) {
  return moment(date, format).isSame(getTomorrow(), 'day');
}

function isInMonth(date: MomentInput, month?: MomentInput) {
  return moment(month).isSame(date, 'month');
}

function getMonthLastDayOrToday(date: MomentInput) {
  return isInMonth(date) ? moment() : getMonthLastDay(date);
}

function getMonthLastDay(date: MomentInput, format?: string) {
  return moment(date, format).endOf('month');
}

function getMonthFirstDay(date: MomentInput, format?: string) {
  return moment(date, format).startOf('month');
}

function getYearFirstDay(date: MomentInput, format?: string) {
  return moment(date, format).startOf('year');
}

function getMiddleDate(
  start: MomentInput,
  end: MomentInput,
  { format }: { format?: string } = {},
) {
  const startDate = moment(start, format);
  const endDate = moment(end, format);

  return startDate.add(endDate.diff(startDate) / 2);
}

function isDayAfterOrSame(day: MomentInput, startDate: MomentInput) {
  return isDayAfter(day, startDate) || isSameDay(day, startDate);
}

function isDayAfter(day: MomentInput, startDate: MomentInput) {
  return moment(day).isAfter(startDate);
}

function isDataBefore(day: MomentInput, endDate: MomentInput) {
  return moment(day).isBefore(endDate);
}

function isSameDay(day: MomentInput, comparisonDay: MomentInput) {
  return moment(day).isSame(comparisonDay, 'day');
}

function isSameMonth(day: MomentInput, comparisonDay: MomentInput) {
  return moment(day).isSame(comparisonDay, 'month');
}

function isSameYear(day: MomentInput, comparisonDay: MomentInput) {
  return moment(day).isSame(comparisonDay, 'year');
}

function isFutureOrToday(day: MomentInput, format?: string) {
  return moment().isSameOrBefore(moment(day, format), 'day');
}

function isFuture(day: MomentInput, format?: string) {
  return moment().isBefore(moment(day, format), 'day');
}

function getHumanizeDay(date: MomentInput, format = localDataFormat) {
  const daysToDate = getDaysToDate(date);
  const defaultDayHandler = () => getFormattedDate(date, format);

  return getHumanizeDayByCount(daysToDate, defaultDayHandler);
}

function getDaysToDate(date: MomentInput) {
  if (!date) {
    return null;
  }

  const formattedDate = getFormattedDate(date);
  const formattedNow = getFormattedToday();

  const daysToDate = moment(formattedDate).diff(formattedNow, 'days', false);

  return daysToDate;
}

function getHumanizeDayByCount(
  daysToDateCount: number | null,
  defaultDayHandler: (daysToDateCount: number | null) => string,
) {
  switch (daysToDateCount) {
    case -1: {
      return 'Yesterday';
    }
    case 0: {
      return 'Today';
    }
    case 1: {
      return 'Tomorrow';
    }
    default: {
      return defaultDayHandler(daysToDateCount);
    }
  }
}

function isDatesValid(dates: Moment[]) {
  return dates.every((date) => date.isValid());
}

function isDatesInRange(
  dates: Moment | Moment[],
  startDate: MomentInput,
  endDate: MomentInput,
) {
  if (Array.isArray(dates)) {
    return dates.every((date) => date.isBetween(startDate, endDate, undefined, '[]'));
  }

  return dates.isBetween(startDate, endDate, undefined, '[]');
}

function calculateFullYears(startDate: MomentInput) {
  if (!startDate) {
    return null;
  }

  const today = moment();
  const fromDate = moment(startDate);
  return today.diff(fromDate, 'years');
}

function getFormattedCurrentDateTime() {
  return moment().format(dateTimeFormatWithTimezone);
}

export {
  getFormattedDate,
  getFormattedToday,
  getFormattedLocalDate,
  getTomorrow,
  getDaysAgo,
  getDayBeforeDate,
  getDayAfterToday,
  getDayAfterDate,
  getMonthsAgo,
  getYearsBeforeDate,
  isToday,
  isTomorrow,
  isInMonth,
  getMonthLastDayOrToday,
  getMonthLastDay,
  getMonthFirstDay,
  getYearFirstDay,
  getMiddleDate,
  isDayAfterOrSame,
  isDayAfter,
  isDataBefore,
  isSameDay,
  isSameMonth,
  isSameYear,
  isFutureOrToday,
  isFuture,
  getHumanizeDay,
  getDaysToDate,
  getHumanizeDayByCount,
  isDatesValid,
  isDatesInRange,
  calculateFullYears,
  getFormattedCurrentDateTime,
  formatDayLabel,
};
